import * as Icons from "lucide-react";
import { Link } from "react-router-dom";
import BlockShell from "./BlockShell";
import { Card, CardContent } from "../ui/card";
import { renderInline } from "./StoryChapterBlock";
import { useState } from "react";
import { absUrl } from "./media";

// Grid of cards (programs, stats, ways to help). Editable via link-list rows.
const FeatureCardsBlock = ({ block }) => {
  const cfg = block.config || {};
  const allCards = cfg.cards || [];
  const collapsedShow = Number(cfg.collapsed_show) || 0;
  const [expanded, setExpanded] = useState(false);
  const cards = collapsedShow && !expanded ? allCards.slice(0, collapsedShow) : allCards;
  const moreBtn = collapsedShow && allCards.length > collapsedShow ? (
    <button type="button" onClick={() => setExpanded(!expanded)} className="mt-4 inline-flex items-center gap-1 text-sm font-bold text-[#1f4d3a] underline-offset-2 hover:underline" data-testid="feature-cards-toggle">
      {expanded ? "Show less" : `Show all ${allCards.length}`} <Icons.ChevronDown className={`h-4 w-4 transition-transform ${expanded ? "rotate-180" : ""}`} />
    </button>
  ) : null;
  const cols = Number(cfg.columns) || 3;
  const compact = cfg.style === "stats";
  const wrapLink = (c, key, inner, cls) => {
    if (!c.link) return <div key={key} className={cls}>{inner}</div>;
    if (c.link.startsWith("#")) return <a key={key} href={c.link} className={cls}>{inner}</a>;
    return /^https?:\/\//i.test(c.link) ? <a key={key} href={c.link} target="_blank" rel="noopener noreferrer" className={cls}>{inner}</a> : <Link key={key} to={c.link} className={cls}>{inner}</Link>;
  };
  if (cfg.style === "list") {
    return (
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-8" data-testid="fest-feature-cards">
      <BlockShell block={block}>
        {cfg.intro && <p className="text-base md:text-lg text-[#5C5C5C] leading-relaxed mb-6 max-w-3xl">{renderInline(cfg.intro, "#1f4d3a")}</p>}
        <ol className="space-y-3" data-testid="feature-list">
          {cards.map((c, i) => {
            const color = c.color || "#2E5C55";
            const inner = (
              <Card className="border-[#E2E0DC] overflow-hidden transition-all hover:shadow-lg hover:-translate-y-0.5" data-testid={`feature-card-${i}`}>
                <CardContent className="p-0 flex">
                  <div className="w-16 md:w-20 shrink-0 text-white flex items-center justify-center font-['Playfair_Display'] text-2xl md:text-3xl font-semibold" style={{ background: color }}>{String(i + 1).padStart(2, "0")}</div>
                  <div className="p-4 md:p-5 flex-1">
                    {c.tag && <div className="text-[10px] font-bold uppercase tracking-widest text-[#E76F51]">{c.tag}</div>}
                    <h3 className="font-['Playfair_Display'] text-lg md:text-xl font-medium text-[#1A1A1A] leading-tight">{c.title}</h3>
                    {c.blurb && <p className="text-sm text-[#5C5C5C] leading-relaxed mt-1.5 whitespace-pre-line">{renderInline(c.blurb, color)}</p>}
                    {c.link && <span className="text-xs font-bold inline-flex items-center gap-1 mt-2" style={{ color }}>{c.link_label || "Open"} <Icons.ArrowRight className="h-3 w-3" /></span>}
                  </div>
                </CardContent>
              </Card>
            );
            return wrapLink(c, i, inner, "block");
          })}
        </ol>
        {moreBtn}
      </BlockShell>
      </section>
    );
  }
  if (cfg.style === "accordion") return <AccordionCards block={block} cfg={cfg} cards={cards} moreBtn={moreBtn} cols={cols} />;

  return (
    <section className="max-w-7xl mx-auto px-6 md:px-12 py-8" data-testid="fest-feature-cards">
    <BlockShell block={block}>
      {cfg.logo && <img src={absUrl(cfg.logo)} alt="" className="h-20 md:h-24 w-auto object-contain mb-4" loading="lazy" data-testid="feature-cards-logo" />}
      {cfg.intro && <p className="text-base text-[#5C5C5C] leading-relaxed mb-6 max-w-3xl">{renderInline(cfg.intro, "#1f4d3a")}</p>}
      <div className={`grid gap-4 sm:grid-cols-2 ${cols >= 4 ? "lg:grid-cols-4" : cols === 2 ? "lg:grid-cols-2" : "lg:grid-cols-3"}`}>
        {cards.map((c, i) => {
          const Icon = Icons[c.icon] || Icons.Star;
          const color = c.color || "#1f4d3a";
          const points = (c.points || "").split("\n").map((p) => p.trim()).filter(Boolean);
          const inner = (
            <Card className="border-[#E2E0DC] h-full overflow-hidden transition-shadow hover:shadow-lg" data-testid={`feature-card-${i}`}>
              {!compact && <div className="h-1.5" style={{ background: color }} />}
              {c.image && <img src={absUrl(c.image)} alt="" className="w-full aspect-video object-cover" loading="lazy" data-testid={`feature-card-image-${i}`} />}
              {c.logo && <div className="px-6 pt-5"><img src={absUrl(c.logo)} alt="" className="h-14 max-w-[70%] object-contain object-left" loading="lazy" data-testid={`feature-card-logo-${i}`} /></div>}
              <CardContent className={compact ? "p-4" : "p-6"}>
                {compact ? (
                  <>
                    <div className="text-2xl sm:text-3xl font-extrabold leading-none" style={{ color }}>{c.title}</div>
                    <div className="text-xs text-[#5C5C5C] mt-1.5">{c.blurb}</div>
                  </>
                ) : (
                  <>
                    <div className="flex items-center gap-3 mb-3">
                      <span className="h-11 w-11 rounded-xl flex items-center justify-center text-white shrink-0" style={{ background: color }}><Icon className="h-5 w-5" /></span>
                      <div>
                        {c.tag && <div className="text-[10px] font-bold uppercase tracking-widest text-[#5C5C5C]">{c.tag}</div>}
                        <h3 className="font-extrabold text-[#1A1A1A] leading-tight">{c.title}</h3>
                      </div>
                    </div>
                    {c.blurb && <p className="text-sm text-[#3a3a3a] leading-relaxed mb-3 whitespace-pre-line">{c.blurb}</p>}
                    {points.length > 0 && (
                      <ul className="space-y-1.5 mb-3">
                        {points.map((p) => <li key={p} className="text-xs text-[#5C5C5C] flex gap-2"><Icons.Star className="h-3.5 w-3.5 text-amber-500 shrink-0 mt-0.5" /> {p}</li>)}
                      </ul>
                    )}
                    {c.link && <span className="text-xs font-bold inline-flex items-center gap-1" style={{ color }}>{c.link_label || "Learn more"} <Icons.ExternalLink className="h-3 w-3" /></span>}
                  </>
                )}
              </CardContent>
            </Card>
          );
          return wrapLink(c, i, inner, "");
        })}
      </div>
      {moreBtn}
    </BlockShell>
    </section>
  );
};

// style "accordion": slim rows (icon · tag · title), text folds open on tap — for pages where big cards fight the owner's photos.
const AccordionCards = ({ block, cfg, cards, moreBtn, cols }) => {
  const [openIdx, setOpenIdx] = useState(null);
  const accent = cfg.accent || "#1A1A1A";
  const target = (c) => (/^https?:\/\//i.test(c.link) ? { href: c.link, target: "_blank", rel: "noopener noreferrer" } : null);
  return (
    <section className="max-w-7xl mx-auto px-6 md:px-12 py-8" data-testid="fest-feature-cards">
      <BlockShell block={block}>
        {cfg.intro && <p className="text-base text-[#5C5C5C] leading-relaxed mb-5 max-w-3xl">{renderInline(cfg.intro, accent)}</p>}
        <div className={`grid gap-2.5 ${cols === 1 ? "" : "md:grid-cols-2"}`} data-testid="feature-accordion">
          {cards.map((c, i) => {
            const Icon = Icons[c.icon] || Icons.Star;
            const color = c.color || accent;
            const open = openIdx === i;
            return (
              <div key={i} className={`rounded-2xl border bg-white transition-colors ${open ? "border-[#1A1A1A]/40 shadow-md" : "border-[#E2E0DC] hover:border-[#1A1A1A]/30"}`} data-testid={`feature-card-${i}`}>
                <button type="button" onClick={() => setOpenIdx(open ? null : i)} aria-expanded={open} className="w-full flex items-center gap-3 px-4 py-3 text-left" data-testid={`feature-card-toggle-${i}`}>
                  <span className="h-9 w-9 rounded-full flex items-center justify-center text-white shrink-0" style={{ background: color }}><Icon className="h-4 w-4" /></span>
                  <span className="min-w-0 flex-1">
                    {c.tag && <span className="block text-[10px] font-bold uppercase tracking-widest" style={{ color }}>{c.tag}</span>}
                    <span className="block font-['Playfair_Display'] text-base md:text-lg font-semibold text-[#1A1A1A] leading-tight">{c.title}</span>
                  </span>
                  <Icons.ChevronDown className={`h-4 w-4 text-[#5C5C5C] shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
                </button>
                {open && (
                  <div className="px-4 pb-4 -mt-1 pl-[4rem]" data-testid={`feature-card-body-${i}`}>
                    {c.blurb && <p className="text-sm text-[#3a3a3a] leading-relaxed whitespace-pre-line m-0">{renderInline(c.blurb, color)}</p>}
                    {c.link && (c.link.startsWith("#") || target(c)
                      ? <a href={c.link} {...(target(c) || {})} className="mt-2 inline-flex items-center gap-1 text-xs font-bold" style={{ color }}>{c.link_label || "Learn more"} <Icons.ArrowRight className="h-3 w-3" /></a>
                      : <Link to={c.link} className="mt-2 inline-flex items-center gap-1 text-xs font-bold" style={{ color }}>{c.link_label || "Learn more"} <Icons.ArrowRight className="h-3 w-3" /></Link>)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {moreBtn}
      </BlockShell>
    </section>
  );
};

export default FeatureCardsBlock;
